import React, { useEffect, useState, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Eager (above-the-fold) — Landing pages render on first paint
import LandingPage from "./pages/LandingPage";
import { MobileLandingPage } from "./pages/mobile";

// Lazy-loaded routes — code-split so the landing-page bundle stays small.
// This dramatically reduces initial JS payload (jspdf, dashboards, results, admin etc.
// no longer ship in the main chunk).
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const Results = lazy(() => import("./pages/Results"));
const DoctorPage = lazy(() => import("./pages/DoctorPage"));
const DoctorDashboard = lazy(() => import("./pages/DoctorDashboard"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const MedicalDisclaimer = lazy(() => import("./pages/MedicalDisclaimer"));
const ImpersonatePage = lazy(() => import("./pages/ImpersonatePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MobileDashboard = lazy(() =>
  import("./pages/mobile").then((m) => ({ default: m.MobileDashboard }))
);
const MobileResults = lazy(() =>
  import("./pages/mobile").then((m) => ({ default: m.MobileResults }))
);

// Hook for mobile detection
import useIsMobile from "./hooks/useIsMobile";

// Hook for page tracking
import usePageTracking from "./hooks/usePageTracking";
import { useCanonical } from "./hooks/useCanonical";
import CookieBanner from "./components/CookieBanner";

// Tiny full-screen fallback shown while a route chunk is being downloaded
const RouteLoader = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Inter', sans-serif",
      color: "#00658D",
    }}
    aria-label="Loading"
    role="status"
  >
    <div
      style={{
        width: 40,
        height: 40,
        border: "3px solid rgba(0,101,141,0.15)",
        borderTopColor: "#00658D",
        borderRadius: "50%",
        animation: "thyroid-spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes thyroid-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pick the API base smartly:
// - On the preview environment the backend lives on a *separate* host
//   (e.g. thyroid-health-check.preview.emergentagent.com) → use BUILD_BACKEND_URL.
// - On production the frontend and backend share the same custom domain. The
//   user may visit the site as `kilpnäärmekliinik.ee` while REACT_APP_BACKEND_URL
//   was baked in as `www.kilpnäärmekliinik.ee` (or vice-versa). A 308 redirect
//   between www / non-www variants strips CORS headers and breaks every API
//   call. To be resilient, when the build URL and current page point at the
//   same domain (ignoring an optional `www.` prefix), prefer the runtime
//   origin so requests stay on the exact host the page was loaded from.
function resolveApiBase() {
  if (typeof window === "undefined") return BUILD_BACKEND_URL;
  if (!BUILD_BACKEND_URL) return window.location.origin;
  try {
    const buildHost = new URL(BUILD_BACKEND_URL).hostname.replace(/^www\./i, "");
    const runtimeHost = window.location.hostname.replace(/^www\./i, "");
    if (buildHost === runtimeHost) return window.location.origin;
  } catch (_) {
    /* fall through */
  }
  return BUILD_BACKEND_URL;
}

const BACKEND_URL = resolveApiBase();
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage to prevent flash
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(() => {
    // If we have stored user, don't show loading
    return !localStorage.getItem("user");
  });

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      
      if (storedToken && storedUser) {
        // Show cached user immediately to prevent flash
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        setLoading(false);
        
        // Always validate and refresh from server
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          localStorage.setItem("user", JSON.stringify(response.data));
        } catch (error) {
          console.error("Auth validation failed:", error);
          if (error.response?.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setToken(null);
            setUser(null);
          }
        }
      } else {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Axios interceptor: handle single-device session enforcement
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.response?.data?.detail === "session_expired_new_login") {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("justLoggedIn");
          setToken(null);
          setUser(null);
          const lang = localStorage.getItem('language') || 'et';
          alert(lang === 'et' 
            ? "Teie seanss on lõpetatud, kuna teie kontole logiti sisse teisest seadmest. Palun logige uuesti sisse."
            : lang === 'ru'
            ? "Ваш сеанс завершён, так как в ваш аккаунт вошли с другого устройства. Пожалуйста, войдите снова."
            : "Your session has been terminated because your account was logged in from another device. Please log in again.");
          window.location.href = "/";
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = (userData, authToken) => {
    console.log("Login called, storing data...");
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
    setUser(userData);
    setToken(authToken);
    setLoading(false);
    console.log("Login complete, user set");
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem("token");
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("justLoggedIn");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Also check localStorage directly as fallback
  const storedUser = localStorage.getItem("user");
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  // Allow access if user is in state OR in localStorage
  if (!user && !storedUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Module-level flag to prevent StrictMode double-processing
let _authCallbackProcessed = false;

// Auth Callback - handles Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (_authCallbackProcessed) return;
    _authCallbackProcessed = true;

    const processSession = async () => {
      const hash = location.hash;
      console.log("Processing auth callback, hash:", hash);
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        console.log("Found session_id, exchanging for token...");
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId }
          );
          
          console.log("Auth session response:", response.data);
          const userData = response.data;
          
          // Check if 2FA is required (all users via Google OAuth)
          if (userData.requires_2fa) {
            console.log("2FA required for Google OAuth user");
            // Redirect doctors/nurses to /doctor, patients to / (landing page)
            const is2faDoctor = userData.is_doctor;
            const targetPath = is2faDoctor ? "/doctor" : "/";
            navigate(targetPath, { 
              replace: true, 
              state: { 
                twoFactorToken: userData.two_factor_token, 
                twoFactorEmail: userData.email,
                fromGoogle: true 
              } 
            });
            return;
          }
          
          // For patients via Google OAuth, create user object with proper structure
          const userObj = {
            user_id: userData.user_id,
            email: userData.email,
            name: userData.name,
            picture: userData.picture,
            created_at: userData.created_at,
            is_doctor: userData.is_doctor || false
          };
          
          // Use the session token for authentication
          console.log("Calling login with token:", userData.token ? "present" : "missing");
          login(userObj, userData.token);
          
          // Navigate directly
          console.log("Navigating to dashboard...");
          navigate("/dashboard", { replace: true });
          
        } catch (error) {
          console.error("Auth error:", error.response?.data || error.message);
          _authCallbackProcessed = false;  // Reset for future login attempts
          setIsProcessing(false);
          navigate("/", { replace: true });
        }
      } else {
        console.log("No session_id found in hash");
        _authCallbackProcessed = false;  // Reset for future login attempts
        setIsProcessing(false);
        navigate("/", { replace: true });
      }
    };

    processSession();
  }, []);

  // Always show loading while processing
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="spinner mx-auto mb-4" />
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Track page views
  usePageTracking();
  
  // Dynamic canonical URLs and noindex for protected pages
  useCanonical();

  // Strip legacy `?lang=*` query parameters from URLs. The i18n language is
  // resolved from localStorage and the blog slugs themselves, so the query
  // parameter is dead weight that creates duplicate URLs in Google Search.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("lang")) return;
    params.delete("lang");
    const qs = params.toString();
    const cleanUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [location.pathname, location.search]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
      {/* Landing - switches between desktop and mobile */}
      <Route path="/" element={isMobile ? <MobileLandingPage /> : <LandingPage />} />
      
      {/* Doctor pages - keep desktop version */}
      <Route path="/doctor" element={<DoctorPage />} />
      <Route path="/impersonate" element={<ImpersonatePage />} />
      <Route
        path="/doctor/dashboard"
        element={
          <ProtectedRoute>
            <DoctorDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Patient Dashboard - switches between desktop and mobile */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {isMobile ? <MobileDashboard /> : <Dashboard />}
          </ProtectedRoute>
        }
      />
      
      {/* Payment pages */}
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      
      {/* Results - switches between desktop and mobile */}
      <Route
        path="/results/:diagnosisId"
        element={
          <ProtectedRoute>
            {isMobile ? <MobileResults /> : <Results />}
          </ProtectedRoute>
        }
      />
      
      {/* Admin page - keep desktop version */}
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public Blog (SEO) */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />

      {/* Legal pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/disclaimer" element={<MedicalDisclaimer />} />

      {/* Real 404 (noindex) — redirecting to "/" made Google report soft-404s */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
